import React, { memo, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { PasswordContainer } from '../../../../components/PasswordContainer';
import { SettingsPageContainer } from '../../../../components/Settings/SettingsPageContainer';
import { ColumnModel, SettingsList } from '../../../../components/Settings/SettingsList';
import { useLocalization } from '../../../../hooks/localization';
import { MappingLogPopupScreen } from '../../Gateway/Setup/SetupMatchScreen';
interface VersionItem {
  id: string;
  deviceTypeId: string;
  deviceName: string;
  deviceTypeName: string;
  version: string;
  description: string;
  error_level: 'green' | 'yellow' | 'red';
  created_timestamp: number;
  updated_timestamp: number;
}

export const DeviceVersionDetailScreen = memo(function DeviceVersionDetailScreen() {
  const { t } = useLocalization();
  const { id: deviceTypeId } = useParams<{ id: string }>();
  
  const deviceVersionList = useSelector(state => state.control.deviceVersionList);
  const bridgeDeviceTypes = useSelector(state => state.control.bridgeDeviceTypeList);
  const [selectedItem, setSelectedItem] = useState<VersionItem | null>(null);
  const [showDescriptionPopup, setShowDescriptionPopup] = useState(false);
  const deviceTypeInfo = bridgeDeviceTypes.find(d => d.id === deviceTypeId);
  const deviceTypeName = deviceTypeInfo?.internal_type_id?.toString() || 'N/A';
  const fallbackDeviceName = deviceVersionList?.device_type_id?.[deviceTypeId]?.[0]?.device_name;
  const deviceName = deviceTypeInfo?.name || fallbackDeviceName || 'Unknown';
  
  const filteredVersions: VersionItem[] = useMemo(() => {

    const versionList = deviceVersionList?.device_type_id?.[deviceTypeId];
    if (!versionList || versionList.length === 0) return [];
    

    const formatTimestamp = (timestamp: number): string => {
      const date = new Date(timestamp);
      const pad = (n: number): string => n.toString().padStart(2, '0');
      return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    };

    return versionList.map(version => {
      const errorLevelMap: Record<string, string> = {
        green: 'info',
        yellow: 'warning',
        red: 'critical',
      };
    
      return {
        ...version,
        deviceTypeId,
        deviceName,
        deviceTypeName,
        error_level_label: errorLevelMap[version.error_level] || 'unknown',
        updated_timestamp: version.updated_timestamp,
        display_updated_timestamp: formatTimestamp(version.updated_timestamp),
      };
    });
    
  }, [deviceVersionList, deviceTypeId, deviceName, deviceTypeName]);

  const columns = useMemo<ColumnModel[]>(() => [
    {
      length: 2,
      name: t('version'),
      key: 'version',
    },
    {
      length: 3,
      name: t('description'),
      key: 'description',
    },
    {
      length: 2,
      name: t('error_level'),
      key: 'error_level_label',
    },
    {
      length: 3,
      name: t('updated_timestamp'),
      key: 'display_updated_timestamp',
    }
  ], [t]);

  return (
    <PasswordContainer requiredRole="service" isFullscreen={true}>
      <SettingsPageContainer
        backUrl="/settings/device/deviceVersionList"
        searchable={false}
        buttons={[]}
        title={deviceName}
      >
        <SettingsList
          columns={columns}
          items={filteredVersions}
          listType="deviceVersionDetail"
          keyExtractor={(item) => item.id}
          selectedItem={selectedItem}
          onItemSelected={(item) => {
            setSelectedItem(item as VersionItem);
            if ((item as VersionItem).description) {
              setShowDescriptionPopup(true);
            }
          }}
        />
        {showDescriptionPopup && selectedItem?.description && (
          <MappingLogPopupScreen
            logs={[selectedItem.description]}
            isDone={true}
            isFormatted={true}
            scrollToBottom={false}
            onClickClose={() => setShowDescriptionPopup(false)}
          />
        )}
      </SettingsPageContainer>
    </PasswordContainer>
  );
});
